import React, { useContext, useState } from 'react'
import { Row, Col, Card, Button } from 'react-bootstrap'
import { Trans } from 'react-i18next'
import PdfViewer from '../PdfViewer'
import { documentTypes } from '../../constants/documentTypes'
import SigningStatus from '../../constants/signingStatuses'
import { useTranslation } from 'react-i18next'
import SigningStage from '../../constants/signingStages'
import { PDFDocumentProxy } from 'pdfjs-dist'
import { isPdf, downloadPdf } from '../../utils/helpers'
import SigningContext from '../../contexts/SigningContext'
import { getZonedTime, findTimeZone } from 'timezone-support'
import appConfig from '../../app.config'
import TagManager from '../TagManager'
import { ITagManagerArgs } from '../../interfaces/i-tag-manager-args'
import { IDataLayer } from '../../interfaces/i-data-layer'
import { GtmEvents } from '../../constants/gtmEvents'

function getCurrentHour(timeZone: string) {
	const zone = findTimeZone(timeZone)
	const date = getZonedTime(new Date(), zone)

    return date.hours;
}

function signingForbidden(country: string) {
	switch (country) {
		case 'lt': {
			const hour = getCurrentHour('Europe/Vilnius')
			return hour >= 22 || hour < 7
		}

		case 'lv': {
			const hour = getCurrentHour('Europe/Riga')
			return hour >= 21 || hour < 8
		}

		default:
			return false
	}
}

function getSigningRestriction(country: string) {
	switch (country) {
		case 'lt':
			return {
				from: '10.00 PM',
				to: '7.00 AM',
			}

		case 'lv':
			return {
				from: '9.00 PM',
				to: '8.00 AM',
			}

		default:
			return null
	}
}

function DefaultSigningBlock() {
	const { country } = appConfig
	const { t } = useTranslation()
	const [pdf, setPdf] = React.useState<PDFDocumentProxy | Error>()
	const { signingRequest, setSigningStage } = useContext(SigningContext)
	const [confirmed, setConfirmed] = useState(false)

    const id = signingRequest?.id ?? ''
	
	const disableSigning = signingForbidden(country)
	const signingRestriction = getSigningRestriction(country)

	function requiresConsumerCreditConfirmation() {
		const supportedMarkets = ['lt', 'lv', 'ee']

		return (
			supportedMarkets.includes(country) &&
			signingRequest?.documentType === documentTypes.promissoryNote
		)
	}

	function signingConfirmed() {
		return !requiresConsumerCreditConfirmation() || confirmed
	}

	const fileName = `${
		signingRequest && t(documentTypes[signingRequest.documentType])
	}_${id}`


    const trigger = React.useCallback(async () => {
        const auth = localStorage.getItem('authMethod');
        const tagManagerArgs = {
            dataLayer: {
                applicationID: signingRequest?.applicationId,
                authenticationMethod: auth,
                event: GtmEvents.AuthenticationSuccess
            } as IDataLayer
        } as ITagManagerArgs;

        TagManager.dataLayer(tagManagerArgs);
    }, [signingRequest]);

    React.useEffect(() => {
        trigger();
    }, [signingRequest, trigger])

    return (
        <Card className="PdfViewer defaultSigningBlock">
            <Card.Header>
                <h2>{signingRequest && t(documentTypes[signingRequest.documentType])}</h2>
            </Card.Header>

            <Card.Body>
                <Row>
                    <Col md={9}>
                        <PdfViewer
                            pdf={pdf}
                            setPdf={setPdf}
                            id={id}
                            status={signingRequest?.status} />
                    </Col>

					<Col md={3}>
						{requiresConsumerCreditConfirmation() && (
							<Row className="m-4">
								<span>
									<Trans
										i18nKey="ContractInfo"
										components={[
											<ul>
												<li></li>
												<li></li>
												<li></li>
												<li></li>
											</ul>,
										]}
									/>
								</span>
							</Row>
						)}

						{!requiresConsumerCreditConfirmation() && (
							<Row className="m-4 signDisclaimer">
								<span>
									<Trans
										i18nKey={
											country === 'de'
												? 'ContractInfoDe'
												: 'Please read the detailed documents carefully, then sign them electronically'
										}
										components={[
											<span className="orange"> </span>,
										]}
									/>
								</span>
							</Row>
						)}

                        {isPdf(pdf) && (
							<Button
								variant="outline-secondary"
								className="my-4 w-100"
								onClick={() => downloadPdf(pdf, `TF Bank ${fileName}.pdf`)}>
								<span className="glyphicon glyphicon-download-alt mr-2" />
								{t("Download PDF document")}
							</Button>
                        )}

						{requiresConsumerCreditConfirmation() && (
							<Row className="m-4">
								<div className="checkbox-confirm">
									<input
										type="checkbox"
										checked={confirmed}
										onChange={() =>
											setConfirmed(!confirmed)
										}
									/>

									<label>{t('ConfirmAgreement')}</label>
								</div>
							</Row>
						)}

						{signingRequest?.status !== SigningStatus.signed && (
							<Button
								variant="primary"
								className="w-100"
								disabled={disableSigning || !signingConfirmed()}
								onClick={() =>
									setSigningStage(SigningStage.signing)
								}>
								{t('Sign electronically')}
							</Button>
						)}

						{disableSigning && signingRestriction && (
							<p className="disabled">
								{t(
									'unfortunately, you cannot sign your contract from {{from}} to {{to}}',
									signingRestriction,
								)}
							</p>
						)}

						{requiresConsumerCreditConfirmation() && (
							<Row className="m-4 signDisclaimer">
								<span>{t('SaveAgreementNote')}</span>
							</Row>
						)}

						<div id="divReturnLink" className="stateSigned">
							<a
								href="/"
								className="btn btn-warning text-uppercase">
								{country === 'de'
									? t('Return to TF Nordic')
									: t('Return to TF Bank')}
							</a>
						</div>
					</Col>

					{isPdf(pdf) && (
						<Col xs={12} className="d-none d-lg-block">
							{disableSigning && signingRestriction &&(
								<p className="disabled">
									{t(
										'unfortunately, you cannot sign your contract from {{from}} to {{to}}',
										signingRestriction,
									)}
								</p>
							)}
							{signingRequest?.status !==
								SigningStatus.signed && (
								<Button
									variant="primary"
									className="w-100 mw300 my-5"
									disabled={
										disableSigning || !signingConfirmed()
									}
									onClick={() =>
										setSigningStage(SigningStage.signing)
									}>
									{t('Sign electronically')}
								</Button>
							)}
						</Col>
					)}
				</Row>
			</Card.Body>
		</Card>
	)
}
export default DefaultSigningBlock